// combatpro/monster/monsterTemplateEngine.js
//
// Combat PRO - Encounter Template engine (Stage 63: Legacy Template System
// Migration).
//
// The pure, framework-independent core of the legacy Encounter Templates subsystem,
// migrated verbatim in behaviour from battletracker.html (the read-only master
// spec). It owns only the data half of the legacy viTpl* helpers: building the
// current-line-up entry list, minting a template record, cloning a template, and the
// summary string. It mirrors the role monsterSelectionEngine.js / monsterStudioEngine.js
// play for their runtimes: data-in / data-out helpers only.
//
// Migrated functions:
//   buildTemplateEntries <- viTplCurrentEntries (battletracker.html 11534): walk the
//                           encounter monsters array, count by selectedMonster (or a
//                           trimmed customName), and return [{ monsterName, count }]
//                           in first-seen order (the legacy Object.keys order).
//   createTemplateRecord <- the viTplSaveCurrent push (11549): a template record
//                           { recordType, id:'TPL-<base36>', name (trimmed), entries,
//                           ts }. id and ts are accepted as input so the factory is
//                           deterministic for tests (the analogue of how
//                           createStudioMonster accepts its id); both default to the
//                           legacy Date.now()-derived values.
//   cloneTemplate        <- viTplDuplicate (11566): viClone the record, mint a fresh
//                           id, append " (Copy)" to the name, refresh ts.
//   templateSummary      <- the viTplRender summary line (11557): join entries as
//                           "N\u00d7 name, ...". React escapes text natively, so the
//                           legacy viEsc is intentionally dropped (the value renders
//                           as a text node, not innerHTML).
//
// What is intentionally NOT here (runtime / UI concerns owned elsewhere, exactly as
// the legacy split would map onto the migrated architecture): the localStorage /
// cloud persistence (viTplPersist / viTplBoot - replaced by the existing Stage 58
// persistence module), the DOM rendering (viTplRender innerHTML - replaced by the
// React modal), the window.prompt / window.confirm / alert prompts, the modal show /
// hide, and the encounter insertion loop (viTplLoad - it drives the Stage 61
// selectMonster from the runtime hook). Those belong to useMonsterStudioTemplates and
// MonsterStudioTemplates.
//
// No React, no JSX, no hooks, no context, no DOM, no querySelector, no innerHTML,
// no fetch, no localStorage, no globals. No optional chaining. No em dash.

// The record discriminator that distinguishes an encounter-template record from a
// custom-monster record in the shared /v1/battletracker/user/monsters store. The
// persistence module imports this to filter templates out of the monster pool and to
// load them back in.
export var ENCOUNTER_TEMPLATE = 'encounter-template';

// Framework-independent template id (legacy 'TPL-' + Date.now().toString(36), 11549).
// Not deterministic; callers that need reproducible ids pass one in.
export function createTemplateId() {
  return 'TPL-' + Date.now().toString(36);
}

// buildTemplateEntries (viTplCurrentEntries, 11534). Count the active encounter
// line-up by name: prefer selectedMonster, fall back to a non-empty trimmed
// customName, ignore empty slots. Entries are returned in first-seen order (matching
// the legacy Object.keys iteration over a plain object keyed by insertion).
export function buildTemplateEntries(monsters) {
  var list = Array.isArray(monsters) ? monsters : [];
  var counts = {};
  var order = [];
  for (var i = 0; i < list.length; i++) {
    var m = list[i];
    var nm = '';
    if (m) {
      if (m.selectedMonster) {
        nm = m.selectedMonster;
      } else if (m.customName && m.customName.trim()) {
        nm = m.customName.trim();
      }
    }
    if (nm) {
      if (counts[nm] == null) { counts[nm] = 0; order.push(nm); }
      counts[nm] = counts[nm] + 1;
    }
  }
  return order.map(function(k) { return { monsterName: k, count: counts[k] }; });
}

// createTemplateRecord (the viTplSaveCurrent push body, 11549). Trims the name like
// the legacy did. id / ts default to the legacy runtime values but may be supplied
// for deterministic tests.
export function createTemplateRecord(name, entries, id, ts) {
  var when = (ts != null) ? ts : Date.now();
  return {
    recordType: ENCOUNTER_TEMPLATE,
    id: id || createTemplateId(),
    name: String(name == null ? '' : name).trim(),
    entries: Array.isArray(entries) ? entries : [],
    ts: when
  };
}

// cloneTemplate (viTplDuplicate, 11566). Deep clone via JSON round-trip (the legacy
// viClone), then a fresh id, the " (Copy)" name suffix, and a refreshed ts. Returns a
// shallow fallback copy if the value is not JSON-serialisable, matching cloneStudioMonster.
export function cloneTemplate(template, id, ts) {
  var src = template || {};
  var copy;
  try { copy = JSON.parse(JSON.stringify(src)); }
  catch (e) { copy = Object.assign({}, src); }
  copy.recordType = ENCOUNTER_TEMPLATE;
  copy.id = id || createTemplateId();
  copy.name = (src.name || '') + ' (Copy)';
  copy.ts = (ts != null) ? ts : Date.now();
  return copy;
}

// templateSummary (the viTplRender summary, 11557). "N\u00d7 name, ..." in entry
// order. Plain text - the React modal renders it as a text node (no viEsc needed).
export function templateSummary(entries) {
  var list = Array.isArray(entries) ? entries : [];
  return list.map(function(e) {
    return (e ? e.count : 0) + '\u00d7 ' + (e ? e.monsterName : '');
  }).join(', ');
}

// isEncounterTemplate: the discriminator predicate used by the persistence filters.
export function isEncounterTemplate(record) {
  return !!(record && record.recordType === ENCOUNTER_TEMPLATE);
}
